import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
} from 'reactstrap';
import * as XLSX from '@e965/xlsx';
import Config from '../../config/Config';
import Loader from '../Loader/Loader';
import SyncFileImportSection from '../SynchronisationPanel/SyncFileImportSection/SyncFileImportSection';

import './ArticleStocksImporter.css';

/** Same spreadsheet set as tickets / promo (SheetJS). */
const FILE_EXTENSIONS = ['csv', 'xls', 'xlsx', 'ods', 'numbers'];
const API_NATIVE_EXTENSIONS = ['csv', 'xls', 'xlsx'];

/**
 * Backend stocks import only accepts csv/xls/xlsx — normalize other SheetJS formats first.
 * @param {File} file
 * @returns {Promise<File>}
 */
const file_for_stocks_api = async file => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (API_NATIVE_EXTENSIONS.includes(extension))
        return file;

    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new File(
        [buffer],
        file.name.replace(/\.[^.]+$/, '.xlsx'),
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
    );
};

const actionBadgeColor = action => {
    switch (action) {
        case 'update':
        case 'updated':
            return 'info';
        case 'skip':
            return 'secondary';
        case 'error':
            return 'danger';
        default:
            return 'light';
    }
};

/**
 * Article stock import UI hosted in the Synchronisation panel.
 *
 * Flow: drop Excel/CSV export → preview update/skip/error → confirm apply.
 *
 * @param {{ visible: boolean }} props
 */
const ArticleStocksImporter = ({ visible }) => {
    const { t, i18n } = useTranslation();
    const { user } = useSelector(state => state.auth);

    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [error, setError] = useState(null);

    const reset = useCallback(() => {
        setFile(null);
        setFileName('');
        setPreview(null);
        setResult(null);
        setError(null);
        setIsPreviewing(false);
        setIsApplying(false);
    }, []);

    const onFileSelected = useCallback(async selected => {
        setError(null);
        setResult(null);
        setPreview(null);

        if (!selected)
            return;

        const extension = selected.name.split('.').pop()?.toLowerCase();
        if (!FILE_EXTENSIONS.includes(extension)) {
            setError(t('articles.stocks_import.unsupported_file_type'));
            setFile(null);
            setFileName('');
            return;
        }

        setFileName(selected.name);
        setIsPreviewing(true);

        try {
            const apiFile = await file_for_stocks_api(selected);
            setFile(apiFile);
            const ArticlesApi = (await import('../../services/rest/Articles')).default;
            const data = await ArticlesApi.previewImportStocks(apiFile);
            setPreview(data);
        } catch (err) {
            setFile(null);
            setPreview(null);
            setError(err?.message || t('articles.stocks_import.preview_failed'));
        } finally {
            setIsPreviewing(false);
        }
    }, [t]);

    const runApply = useCallback(async () => {
        if (!file || isApplying)
            return;

        setIsApplying(true);
        setError(null);
        try {
            const ArticlesApi = (await import('../../services/rest/Articles')).default;
            const data = await ArticlesApi.importStocks(file);
            setResult(data);
            setPreview(null);
        } catch (err) {
            setError(err?.message || t('articles.stocks_import.apply_failed'));
        } finally {
            setIsApplying(false);
        }
    }, [file, isApplying, t]);

    if (!visible)
        return null;

    const counts = preview?.counts || result?.counts || {};
    const rows = preview?.rows || result?.rows || [];
    const updateCount = Number(counts.update || 0);
    const canApply = !!preview && updateCount > 0 && !isApplying && !isPreviewing;
    const isBusy = isPreviewing || isApplying;
    const lang = String(i18n.language || 'fr').split(/[-_]/)[0];
    const stocksExportUrl = user?.api_key
        ? `${Config.api_end_point}/article/export_stocks?x-api-key=${user.api_key}&lang=${encodeURIComponent(lang)}`
        : null;

    return (
        <div className="ArticleStocksImporter">
            <SyncFileImportSection
                title={t('articles.stocks_import.upload_title')}
                help={t('articles.stocks_import.upload_help')}
                formats={t('common.supported_spreadsheet_formats')}
                accept=".csv,.xls,.xlsx,.ods,.numbers"
                disabled={isBusy}
                fileName={fileName}
                onFile={onFileSelected}
                downloadSlot={stocksExportUrl ? (
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={stocksExportUrl}
                        className="btn btn-info btn-sm"
                    >
                        <i className="fa fa-download" />
                        &nbsp;{t('articles.stocks_import.download_current')}
                    </a>
                ) : null}
            >
                {(preview || result || error) && (
                    <Button color="secondary" size="sm" className="mt-3" onClick={reset} disabled={isBusy}>
                        {t('articles.stocks_import.reset')}
                    </Button>
                )}
                {error && <Alert color="danger" className="mt-3 mb-0">{error}</Alert>}
            </SyncFileImportSection>

            {isBusy && (
                <div className="mb-3">
                    <Loader />
                    {isApplying && <p className="text-muted mt-2">{t('articles.stocks_import.applying')}</p>}
                </div>
            )}

            {preview && (
                <Card className="mb-3">
                    <CardBody>
                        <h5>{t('articles.stocks_import.preview_title')}</h5>
                        <p>
                            {t('articles.stocks_import.preview_summary', {
                                update: counts.update || 0,
                                skip: counts.skip || 0,
                                error: counts.error || 0,
                            })}
                        </p>
                        {updateCount > 0 ? (
                            <Alert color="success" className="mb-3">
                                {t('articles.stocks_import.ready')}
                            </Alert>
                        ) : (
                            <Alert color="info" className="mb-3">
                                {t('articles.stocks_import.nothing_to_update')}
                            </Alert>
                        )}
                        <div className="ArticleStocksImporter-tableWrap">
                            <table className="table table-sm w-100">
                                <thead>
                                    <tr>
                                        <th>{t('articles.stocks_import.col_action')}</th>
                                        <th>{t('articles.stocks_import.col_article')}</th>
                                        <th>{t('articles.stocks_import.col_variant')}</th>
                                        <th>{t('articles.stocks_import.col_salepoints')}</th>
                                        <th>{t('articles.stocks_import.col_current')}</th>
                                        <th>{t('articles.stocks_import.col_new')}</th>
                                        <th>{t('articles.stocks_import.col_reason')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, index) => (
                                        <tr key={`${row.article_id}-${row.variant_id}-${index}`}>
                                            <td>
                                                <Badge color={actionBadgeColor(row.action)}>
                                                    {t(`articles.stocks_import.action_${row.action}`)}
                                                </Badge>
                                            </td>
                                            <td>{row.article_name || row.article_id}</td>
                                            <td>{row.variant_name || row.variant_id || '—'}</td>
                                            <td>{row.salepoints || (row.salepoint_ids || []).join(', ')}</td>
                                            <td>{row.current_stock ?? '—'}</td>
                                            <td>{row.new_stock ?? row.stock ?? '—'}</td>
                                            <td>{row.reason || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Button color="success" className="mt-2" onClick={runApply} disabled={!canApply}>
                            <i className={`fa ${isApplying ? 'fa-spinner fa-spin' : 'fa-upload'}`} />
                            &nbsp;{t('articles.stocks_import.do_import')}
                        </Button>
                    </CardBody>
                </Card>
            )}

            {result && (
                <Card className="mb-3">
                    <CardBody>
                        <h5>{t('articles.stocks_import.result_title')}</h5>
                        <Alert color={result.counts?.error ? 'warning' : 'success'}>
                            {t('articles.stocks_import.result_summary', {
                                updated: result.counts?.updated || 0,
                                skipped: result.counts?.skip || 0,
                                error: result.counts?.error || 0,
                            })}
                        </Alert>
                        {rows.some(row => row.action === 'error') && (
                            <ul>
                                {rows.filter(row => row.action === 'error').map((row, index) => (
                                    <li key={`fail-${index}`}>
                                        <strong>{row.article_name || row.article_id}</strong>
                                        {row.reason ? `: ${row.reason}` : ''}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </CardBody>
                </Card>
            )}
        </div>
    );
};

export default ArticleStocksImporter;
